/* -------------------------------------------------------------------------- */
/*                                Dependencies                                */
/* -------------------------------------------------------------------------- */

// React
import { useState } from 'react';

// Mantine
import {
	Box,
	Group,
	Title,
	Radio as NativeRadio,
	Tooltip,
} from '@mantine/core';

// Components
import { Select } from '@components/atoms/Select';
import { Button } from '@components/atoms/Button';
import { Text } from '@components/atoms/Text';

// Styles
import classes from './EditOrganizationForm.module.css';

// Utils
import {
	cleanObject,
	convertUsersToLabelValue,
} from '@utils/functions';

// Icons
import useStore from '@store/index';

// I18n
import { TextInput } from '@components/atoms/TextInput';
import { useTranslation } from 'react-i18next';
import {
	Divider,
	Radio,
	TextInputV2,
} from '@components/atoms';
import { useMediaQuery } from '@mantine/hooks';
import {
	matches,
	useForm,
	UseFormReturnType,
} from '@mantine/form';
import {
	TFormValuesContact,
	TFormValuesOrganization,
} from '@features/resources/organizations/types';
import { useUsers } from '@api/get-users';
import { useAddUser } from '@api/create-user';
import {
	REGEX_EMAIL,
	REGEX_PHONE_NUMBER,
} from '@config/constants/regex';
import { NumberInput } from '@components/atoms/NumberInput';
import { ALL_COUNTRIES } from '@config/constants/all-countries';
import { useUpdateOrganization } from '@features/resources/organizations/api/update-organization';
import { PictureUpload } from '@components/molecules/PictureUpload/PictureUpload';
import { useUploadPicture } from '@api/upload-picture';
import isNil from 'lodash/isNil';

/* -------------------------------------------------------------------------- */
/*                                  Component                                 */
/* -------------------------------------------------------------------------- */
export const EditOrganizationForm = (props: {
	form: UseFormReturnType<TFormValuesOrganization>;
	onCancel: () => void;
	onBack: () => void;
	open: boolean;
}) => {
	const [addContactType, setAddContactType] = useState(
		'existingContact'
	);

	const [pictureData, setPictureData] =
		useState<null | FormData>(null);

	const [ownerId, setOwnerId] = useState<null | string>(
		null
	);

	const {
		organizationsPage,
		organizationsPageSize,
		organizationsSelectedRow,
	} = useStore();

	const { t } = useTranslation();

	const { data: users } = useUsers(
		{ enabled: props.open },
		{
			organization: organizationsSelectedRow.id,
		}
	);

	const uploadPictureMutation = useUploadPicture({
		mutationConfig: {
			onSuccess(data) {
				props.form.validate();
				if (props.form.isValid()) {
					const {
						name,
						countryCode,
						place,
						owner,
						obfuscateIPFS,
					} = props.form.values;

					updateOrganization.mutate({
						data: {
							...(props.form.isDirty('logo')
								? {
										logo: data.id,
									}
								: {}),
							...(props.form.isDirty('name')
								? {
										name,
									}
								: {}),
							...(props.form.isDirty('owner')
								? {
										owner,
									}
								: {}),
							...(props.form.isDirty('obfuscateIPFS')
								? {
										obfuscateIPFS:
											obfuscateIPFS === 'true',
									}
								: {}),
							...(props.form.isDirty('countryCode') ||
							props.form.isDirty('place')
								? {
										location: {
											countryCode: countryCode,
											place: place,
										},
									}
								: {}),
						},
						id: organizationsSelectedRow.id,
					});
				}
			},
		},
	});

	const updateOrganization = useUpdateOrganization({
		page: organizationsPage,
		size: organizationsPageSize,
		mutationConfig: {
			onSuccess() {
				props.onBack();
			},
		},
	});

	const addUser = useAddUser({
		page: organizationsPage,
		size: organizationsPageSize,
		mutationConfig: {
			onSuccess(data) {
				setOwnerId(data.id);
				if (pictureData) {
					uploadPictureMutation.mutate(pictureData);
				} else {
					const { name, countryCode, place } =
						props.form.getValues();

					updateOrganization.mutate({
						data: cleanObject({
							...(props.form.isDirty('name')
								? {
										name,
									}
								: {}),
							owner: data.id,
							...(props.form.isDirty('countryCode') ||
							props.form.isDirty('place')
								? {
										location: {
											countryCode: countryCode,
											place: place,
										},
									}
								: {}),
						}),
						id: organizationsSelectedRow.id,
					});
				}
			},
		},
	});

	const handleOnUploadProfile = (
		formData: FormData | string | null
	) => {
		if (typeof formData !== 'string' && formData) {
			props.form.setFieldValue('logo', formData);
			setPictureData(formData);
		} else {
			props.form.setFieldValue('logo', null);
		}
	};

	const getForm = () => {
		/* ******************************** HELPERS *********************************** */

		const handleSubmitForm = () => {
			if (
				props.form.getValues().logo !== null &&
				props.form.isDirty('logo') &&
				props.form.isValid()
			) {
				uploadPictureMutation.mutate(pictureData);
			} else {
				props.form.validate();
				if (props.form.isValid()) {
					const {
						name,
						countryCode,
						place,
						owner,
						obfuscateIPFS,
					} = props.form.values;

					updateOrganization.mutate({
						data: {
							...(props.form.isDirty('name')
								? {
										name,
									}
								: {}),
							...(props.form.isDirty('logo')
								? {
										logo: null,
									}
								: {}),
							...(props.form.isDirty('owner')
								? {
										owner,
									}
								: {}),
							...(props.form.isDirty('obfuscateIPFS')
								? {
										obfuscateIPFS:
											obfuscateIPFS === 'true',
									}
								: {}),
							...(props.form.isDirty('countryCode') ||
							props.form.isDirty('place')
								? {
										location: {
											countryCode: countryCode,
											place: place,
										},
									}
								: {}),
						},
						id: organizationsSelectedRow.id,
					});
				}
			}
		};

		/* ******************************** Media Query ******************************* */
		const isBigScreen = useMediaQuery(
			'(min-width: 120em)'
		);

		// Validators
		const formContact = useForm<TFormValuesContact>({
			initialValues: {
				firstName: '',
				lastName: '',
				email: '',
				language: 'fr',
				phoneNumber: '',
			},
			validate: {
				firstName: (value) =>
					value.length < 2
						? t('validations.owner.firstName')
						: null,
				lastName: (value) =>
					value.length < 2
						? t('validations.owner.lastName')
						: null,

				email: matches(
					REGEX_EMAIL,
					'form.error.invalidEmail'
				),
				phoneNumber: matches(
					REGEX_PHONE_NUMBER,
					'form.error.invalidPhoneNumber'
				),
			},
		});
		/* ******************************** RENDERING ******************************* */
		return (
			<form
				className={classes.containerForm}
				onSubmit={props.form.onSubmit(() => {})}
			>
				<Box className={classes.containerTitle}>
					<Title className={classes.titleForm}>
						{props.form.getValues().name
							? props.form.getValues().name
							: t('global.organizations')}
					</Title>
					<Text className={classes.subTitleForm}>
						/ {t('organizations.updateOrganization')}
					</Text>
				</Box>

				<PictureUpload
					onUpload={handleOnUploadProfile}
					src={organizationsSelectedRow?.logo || ''}
				/>
				<Box className={classes.row}>
					<Box className={classes.containerInput}>
						<TextInput
							withAsterisk
							visibleFrom="xxl"
							size="md"
							label={t('global.organizationName')}
							placeholder={t('global.organizationName')}
							{...props.form.getInputProps('name')}
						/>
						<TextInput
							withAsterisk
							hiddenFrom="xxl"
							size="sm"
							label={t('global.name')}
							placeholder={t('global.name')}
							{...props.form.getInputProps('name')}
						/>
					</Box>
				</Box>
				{/* Row for Country & Place */}
				<Box className={classes.row}>
					<Box className={classes.containerInput}>
						<Select
							withAsterisk
							size={isBigScreen ? 'md' : 'sm'}
							label={t('global.country')}
							data={ALL_COUNTRIES}
							placeholder={t('placeholders.country')}
							{...props.form.getInputProps('countryCode')}
						/>
					</Box>
					<Box className={classes.containerInput}>
						<TextInputV2
							disabled={!props.form.values.countryCode}
							withAsterisk
							size={isBigScreen ? 'md' : 'sm'}
							label={t('global.place')}
							placeholder={t('global.place')}
							{...props.form.getInputProps('place')}
						/>
					</Box>
				</Box>
				<Box className={classes.row}>
					<Tooltip label={t('global.comingSoon')}>
						<Box className={classes.containerInput}>
							<NumberInput
								disabled
								hideControls
								maxLength={9}
								withAsterisk
								visibleFrom="xxl"
								size="md"
								label={t('global.legalIdentifier')}
								placeholder={t('global.legalIdentifier')}
								{...props.form.getInputProps(
									'legalIdentifier'
								)}
							/>
							<NumberInput
								disabled
								hideControls
								maxLength={9}
								withAsterisk
								hiddenFrom="xxl"
								size="sm"
								label={t('global.legalIdentifier')}
								placeholder={t('global.legalIdentifier')}
								{...props.form.getInputProps(
									'legalIdentifier'
								)}
							/>
						</Box>
					</Tooltip>
				</Box>
				<Divider size="xs" />
				<Box className={classes.row}>
					<Text className={classes.titleAddContact}>
						{t('global.owner')}
					</Text>
				</Box>
				<>
					{organizationsSelectedRow?.owner?.id ? (
						<Box className={classes.row}>
							<Box className={classes.containerInput}>
								<Select
									disabled
									visibleFrom="xxl"
									size="md"
									label={t('global.owner')}
									withAsterisk
									data={convertUsersToLabelValue(users)}
									placeholder={t('global.owner')}
									{...props.form.getInputProps('owner')}
								/>
								<Select
									disabled
									hiddenFrom="xxl"
									size="sm"
									label={t('global.owner')}
									withAsterisk
									data={convertUsersToLabelValue(users)}
									placeholder={t('global.owner')}
									{...props.form.getInputProps('owner')}
								/>
							</Box>
						</Box>
					) : (
						<>
							<Box className={classes.row}>
								<NativeRadio.Group
									value={addContactType}
									name="addProject"
									withAsterisk
									onChange={setAddContactType}
								>
									<Group gap={'47px'}>
										<Radio
											variant="outline"
											size={isBigScreen ? 'sm' : 'xs'}
											label={t(
												'global.selectAnExistingContact'
											)}
											value={'existingContact'}
										/>
										<Radio
											variant="outline"
											size={isBigScreen ? 'sm' : 'xs'}
											label={t('global.addNewContact')}
											value={'newContact'}
										/>
									</Group>
								</NativeRadio.Group>
							</Box>
							{addContactType == 'existingContact' ? (
								<Box className={classes.row}>
									<Box className={classes.containerInput}>
										<Select
											visibleFrom="xxl"
											size="md"
											label={t('global.owner')}
											withAsterisk
											data={convertUsersToLabelValue(
												users
											)}
											placeholder={t('global.owner')}
											{...props.form.getInputProps(
												'owner'
											)}
										/>
										<Select
											hiddenFrom="xxl"
											size="sm"
											label={t('global.owner')}
											withAsterisk
											data={convertUsersToLabelValue(
												users
											)}
											placeholder={t('global.owner')}
											{...props.form.getInputProps(
												'owner'
											)}
										/>
									</Box>
								</Box>
							) : (
								<>
									<Box className={classes.row}>
										<Box
											className={classes.containerInput}
										>
											<TextInputV2
												maxLength={100}
												withAsterisk
												size={isBigScreen ? 'md' : 'sm'}
												label={t('global.firstName')}
												placeholder={t(
													'global.firstName'
												)}
												{...formContact.getInputProps(
													'firstName'
												)}
											/>
										</Box>
										<Box
											className={classes.containerInput}
										>
											<TextInputV2
												maxLength={100}
												withAsterisk
												size={isBigScreen ? 'md' : 'sm'}
												label={t('global.lastName')}
												placeholder={t('global.lastName')}
												{...formContact.getInputProps(
													'lastName'
												)}
											/>
										</Box>
									</Box>
									<Box className={classes.row}>
										<Box
											className={classes.containerInput}
										>
											<TextInputV2
												withAsterisk
												size={isBigScreen ? 'md' : 'sm'}
												label={t('global.email')}
												placeholder={t('global.email')}
												{...formContact.getInputProps(
													'email'
												)}
											/>
										</Box>
									</Box>
									<Box className={classes.row}>
										<Box
											style={{
												width: '30%',
											}}
										>
											<NativeRadio.Group
												withAsterisk
												label={t('global.language')}
												{...formContact.getInputProps(
													'language'
												)}
											>
												<Group gap={'25px'} pt={'md'}>
													<NativeRadio
														variant="outline"
														size={
															isBigScreen ? 'sm' : 'xs'
														}
														label={'Fr'}
														value={'fr'}
													/>
													<NativeRadio
														variant="outline"
														size={
															isBigScreen ? 'sm' : 'xs'
														}
														label={'En'}
														value={'en'}
													/>
												</Group>
											</NativeRadio.Group>
										</Box>
										<Box
											className={classes.containerInput}
										>
											<TextInputV2
												size={isBigScreen ? 'md' : 'sm'}
												label={t('global.phoneNumber')}
												placeholder={t(
													'global.phoneNumber'
												)}
												{...formContact.getInputProps(
													'phoneNumber'
												)}
											/>
										</Box>
									</Box>
								</>
							)}
						</>
					)}
				</>
				{/* Row for Buttons */}
				<Box className={classes.rowButtons}>
					<Box className={classes.containerButtonCancel}>
						<Button
							fullWidth
							hiddenFrom="xxl"
							size="md"
							variant="transparent"
							onClick={() => {
								props.onCancel();
							}}
						>
							{t('global.cancel')}
						</Button>
						<Button
							fullWidth
							size="lg"
							variant="transparent"
							visibleFrom="xxl"
							onClick={() => {
								props.onCancel();
							}}
						>
							{t('global.cancel')}
						</Button>
					</Box>
					<Box className={classes.containerButton}>
						<Button
							disabled={
								(!props.form.isDirty() &&
									(addContactType == 'newContact'
										? !formContact.isDirty()
										: true)) ||
								updateOrganization.isLoading
							}
							fullWidth
							size={isBigScreen ? 'lg' : 'md'}
							onClick={() => {
								if (
									addContactType !== 'existingContact'
								) {
									props.form.validate();
									formContact.validate();

									if (formContact.isValid()) {
										const values =
											formContact.getValues();
										const { phoneNumber, ...restValues } =
											values;
										addUser.mutate({
											organization:
												organizationsSelectedRow.id,
											...restValues,
											...(phoneNumber !== ''
												? {
														phoneNumber,
													}
												: {}),
										});
									}
								} else {
									handleSubmitForm();
								}
							}}
							loading={updateOrganization.isLoading}
						>
							{t('organizations.updateOrganization')}
						</Button>
					</Box>
				</Box>
			</form>
		);
	};

	/* ******************************** RENDERING ******************************* */
	return (
		<Box className={classes.container}>
			<Box className={classes.containerEmpty} />
			{getForm()}
			<Box className={classes.containerEmpty} />
		</Box>
	);
};
